import { createFileRoute, useRouter, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, Search, Plus, X, CheckCircle2, Copy, Mail, Send } from "lucide-react";
import { listCustomers } from "@/lib/admin.functions";
import { createCustomerInvitation, listCustomerProjects, listPortalInviteCandidates, resendCustomerInvitation } from "@/lib/portal/admin.functions";
import { z } from "zod";
import { redirect } from "@tanstack/react-router";

export const Route = createFileRoute("/_authenticated/admin/customers/")({
  component: CustomersPage,
  validateSearch: z.object({ tab: z.enum(["customers"]).optional() }),
  beforeLoad: ({ search }) => {
    if ((search as any)?.tab === "leads") {
      throw redirect({ to: "/admin/estimates" });
    }
  },
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-20 text-center">
        <p className="text-red-400 mb-3">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function CustomersPage() {
  const fn = useServerFn(listCustomers);
  const projectsFn = useServerFn(listCustomerProjects);
  const resendFn = useServerFn(resendCustomerInvitation);
  const [search, setSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState<{ email?: string; fullName?: string; phone?: string } | null>(null);
  const [resending, setResending] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const isCustomers = true;
  const { data, isLoading } = useQuery({ queryKey: ["customers", search], queryFn: () => fn({ data: { search, limit: 200 } }), enabled: isCustomers });
  const { data: projects } = useQuery({ queryKey: ["customer-projects"], queryFn: () => projectsFn(), enabled: isCustomers });

  const openInvite = (prefill?: { email?: string; fullName?: string; phone?: string }) => { setInvitePrefill(prefill ?? null); setInviteOpen(true); };
  const closeInvite = () => { setInviteOpen(false); setInvitePrefill(null); };

  const hasProject = (email: string | null | undefined) =>
    !!email && !!projects?.some((p: any) => p.customer_email?.toLowerCase() === email.toLowerCase());

  const projectForEmail = (email: string | null | undefined) =>
    email ? projects?.find((p: any) => p.customer_email?.toLowerCase() === email.toLowerCase()) : undefined;

  const doResend = async (projectId: string) => {
    setResending(projectId); setResendMsg(null);
    try {
      const res = await resendFn({ data: { projectId } });
      const link = `${window.location.origin}/portal/claim/${res.token}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      setResendMsg(res.emailQueued ? "Invitation re-sent. Link copied to clipboard." : "Invitation link copied, but email could not be queued.");
    } catch (e) {
      setResendMsg((e as Error).message);
    } finally {
      setResending(null);
      setTimeout(() => setResendMsg(null), 4000);
    }
  };

  return (
    <div className="space-y-6">
      {resendMsg && (
        <div className="fixed bottom-6 right-6 z-50 rounded-xl border border-amber-300/30 bg-[#0a0a0a] px-4 py-3 text-sm text-amber-200 shadow-xl max-w-sm">
          {resendMsg}
        </div>
      )}
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-amber-300 text-[10px] sm:text-xs font-semibold tracking-widest uppercase">Portal</p>
          <h1 className="font-serif text-2xl sm:text-3xl text-white mt-1">Customer Projects</h1>
          <p className="text-xs text-zinc-500 mt-1">Portal invitations, proposals, and invoices tied to each project. For raw estimate leads, see <a href="/admin/estimates" className="text-amber-300 hover:underline">AI Estimates</a>.</p>
        </div>
        {isCustomers && (
          <button onClick={() => openInvite()} className="bg-amber-300 text-zinc-950 font-semibold text-sm px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:bg-amber-200 transition w-full sm:w-auto justify-center min-h-[44px]">
            <Plus className="size-4" /> Invite to portal
          </button>
        )}
      </header>

      {isCustomers && projects && projects.length > 0 && (
        <section>
          <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Portal projects</h2>
          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {projects.map((p) => (
              <Link key={p.id} to="/admin/customers/$projectId" params={{ projectId: p.id }} className="block rounded-2xl border border-white/10 bg-white/[0.02] p-4 active:bg-white/[0.04]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium truncate">{p.project_name}</p>
                    <p className="text-xs text-zinc-400 truncate">{p.customer_name || "—"} · {p.customer_email}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-300/10 text-amber-200 border border-amber-300/20 shrink-0">{p.status}</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] text-zinc-500">
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                  {p.customer_user_id
                    ? <span className="text-amber-300 inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> Claimed</span>
                    : <span>Pending claim</span>}
                </div>
                {!p.customer_user_id && (
                  <div className="mt-3">
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); doResend(p.id); }} disabled={resending === p.id} className="w-full text-xs font-semibold text-amber-200 bg-amber-300/10 hover:bg-amber-300/20 border border-amber-300/30 rounded-full px-3 py-2 inline-flex items-center justify-center gap-2 disabled:opacity-40">
                      {resending === p.id ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />}
                      Resend invitation
                    </button>
                  </div>
                )}
              </Link>
            ))}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Project</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-left px-4 py-3">Claimed?</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <Link to="/admin/customers/$projectId" params={{ projectId: p.id }} className="text-white hover:text-amber-300">
                        {p.project_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{p.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{p.customer_email}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-300/10 text-amber-200 border border-amber-300/20">{p.status}</span></td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {p.customer_user_id ? <span className="text-amber-300 text-xs inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> Claimed</span> : <span className="text-xs text-zinc-500">Pending</span>}
                      <Link to="/admin/customers/$projectId" params={{ projectId: p.id }} className="ml-3 text-[11px] text-amber-300 hover:underline">Manage →</Link>
                      {!p.customer_user_id && (
                        <button onClick={() => doResend(p.id)} disabled={resending === p.id} className="ml-3 text-[11px] text-amber-200 hover:text-amber-100 inline-flex items-center gap-1 disabled:opacity-40">
                          {resending === p.id ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />} Resend
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {isCustomers && <>
      <div className="relative w-full sm:max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email…"
          className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-base sm:text-sm text-white min-h-[44px]"
        />
      </div>

      {isLoading || !data ? <div className="py-20 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div> : (
        <>
        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {data.length === 0
            ? <p className="text-center text-zinc-500 text-sm py-10">No customers yet.</p>
            : data.map((c: any) => (
              <article key={c.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-white truncate">{c.full_name || c.email || "—"}</p>
                    {c.full_name && <p className="text-[11px] text-zinc-500 truncate">{c.email}</p>}
                    {c.roles.length > 0 && <p className="text-[10px] text-amber-300 uppercase tracking-wider">{c.roles.join(", ")}</p>}
                  </div>
                  <p className="text-white font-semibold shrink-0">${(c.lifetime_value_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-zinc-400">
                  <div><p className="text-zinc-400 uppercase tracking-wider text-[9px]">Projects</p><p className="text-zinc-200">{c.projects_count}</p></div>
                  <div><p className="text-zinc-400 uppercase tracking-wider text-[9px]">Estimates</p><p className="text-zinc-200">{c.estimates_count}</p></div>
                  <div><p className="text-zinc-400 uppercase tracking-wider text-[9px]">Last sign-in</p><p className="text-zinc-200">{c.last_sign_in_at ? new Date(c.last_sign_in_at).toLocaleDateString() : "never"}</p></div>
                </div>
                {c.latest_status && <p className="mt-2 text-[11px] text-zinc-500">Status: <span className="text-zinc-300">{c.latest_status}</span></p>}
                <div className="mt-3 flex justify-end">
                  {hasProject(c.email)
                    ? (() => { const pr = projectForEmail(c.email); return (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-amber-300/80 inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> Invited</span>
                          {pr && !pr.customer_user_id && (
                            <button onClick={() => doResend(pr.id)} disabled={resending === pr.id} className="text-xs font-semibold text-amber-200 bg-amber-300/10 hover:bg-amber-300/20 border border-amber-300/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40 min-h-[36px]">
                              {resending === pr.id ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />} Resend
                            </button>
                          )}
                        </div>
                      ); })()
                    : <button onClick={() => openInvite({ email: c.email, fullName: c.full_name, phone: c.phone })} className="text-xs font-semibold text-zinc-950 bg-amber-300 hover:bg-amber-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1 min-h-[36px]"><Mail className="size-3" /> Invite to portal</button>}
                </div>
              </article>
            ))}
        </div>
        {/* Desktop table */}
        <div className="hidden md:block rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-left px-4 py-3">Last sign-in</th>
                <th className="text-right px-4 py-3">Projects</th>
                <th className="text-right px-4 py-3">Estimates</th>
                <th className="text-right px-4 py-3">Lifetime value</th>
                <th className="text-left px-4 py-3">Latest status</th>
                <th className="text-right px-4 py-3">Portal</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500 text-sm">No customers yet.</td></tr>
              ) : data.map((c: any) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="px-4 py-3">
                    <p className="text-white">{c.full_name || c.email || "—"}</p>
                    {c.full_name && <p className="text-[11px] text-zinc-500">{c.email}</p>}
                    {c.roles.length > 0 && <p className="text-[10px] text-amber-300 uppercase tracking-wider">{c.roles.join(", ")}</p>}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{c.last_sign_in_at ? new Date(c.last_sign_in_at).toLocaleDateString() : "never"}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{c.projects_count}</td>
                  <td className="px-4 py-3 text-right text-zinc-300">{c.estimates_count}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">${(c.lifetime_value_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{c.latest_status || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {hasProject(c.email)
                      ? (() => { const pr = projectForEmail(c.email); return (
                          <div className="inline-flex items-center gap-2">
                            <span className="text-[10px] uppercase tracking-wider text-amber-300/80 inline-flex items-center gap-1"><CheckCircle2 className="size-3" /> Invited</span>
                            {pr && !pr.customer_user_id && (
                              <button onClick={() => doResend(pr.id)} disabled={resending === pr.id} className="text-xs font-semibold text-amber-200 bg-amber-300/10 hover:bg-amber-300/20 border border-amber-300/30 rounded-full px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40">
                                {resending === pr.id ? <Loader2 className="size-3 animate-spin" /> : <Send className="size-3" />} Resend
                              </button>
                            )}
                          </div>
                        ); })()
                      : <button onClick={() => openInvite({ email: c.email, fullName: c.full_name, phone: c.phone })} className="text-xs font-semibold text-zinc-950 bg-amber-300 hover:bg-amber-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1"><Mail className="size-3" /> Invite</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
      </>}

      {inviteOpen && <InviteModal onClose={closeInvite} initialEmail={invitePrefill?.email} initialFullName={invitePrefill?.fullName} initialPhone={invitePrefill?.phone} />}
    </div>
  );
}

function InviteModal({ onClose, initialEmail, initialFullName, initialPhone }: { onClose: () => void; initialEmail?: string; initialFullName?: string; initialPhone?: string }) {
  const fn = useServerFn(createCustomerInvitation);
  const candidatesFn = useServerFn(listPortalInviteCandidates);
  const qc = useQueryClient();
  type InviteForm = { email: string; fullName: string; phone?: string; address?: string; projectName: string; leadId?: string; estimateId?: string; proposalId?: string };
  const [form, setForm] = useState<InviteForm>({ email: initialEmail ?? "", fullName: initialFullName ?? "", phone: initialPhone ?? "", address: "", projectName: "" });
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);

  type PickItem = { key: string; kind: "customer" | "lead"; name: string; email: string; phone?: string; address?: string; projectType?: string; projectName?: string; leadId?: string; estimateId?: string; proposalId?: string };
  const { data: candidateData, isFetching: candidatesLoading, error: candidatesError } = useQuery({
    queryKey: ["portal-invite-candidates", pickerQuery],
    queryFn: () => candidatesFn({ data: { search: pickerQuery, limit: 25 } }),
    staleTime: 30_000,
  });
  const filtered = ((candidateData ?? []) as PickItem[]).slice(0, 8);

  const pick = (i: PickItem) => {
    setForm((f) => ({
      ...f,
      email: i.email || f.email,
      fullName: i.name || f.fullName,
      phone: i.phone || f.phone,
      address: i.address || f.address,
      projectName: i.projectName || f.projectName || (i.projectType ? `${i.projectType.charAt(0).toUpperCase()}${i.projectType.slice(1)} project` : f.projectName),
      leadId: i.leadId || f.leadId,
      estimateId: i.estimateId || f.estimateId,
      proposalId: i.proposalId || f.proposalId,
    }));
    setPickerQuery(`${i.name || i.email}`);
    setPickerOpen(false);
  };

  // Autofill name/phone/project when the typed email matches a known customer, project, proposal, or lead.
  useEffect(() => {
    const email = form.email.trim().toLowerCase();
    if (!email) return;
    const match = ((candidateData ?? []) as PickItem[]).find((i) => i.email.toLowerCase() === email);
    if (!match) return;
    setForm((f) => ({
      ...f,
      fullName: f.fullName || match.name || "",
      phone: f.phone || match.phone || "",
      address: f.address || match.address || "",
      projectName: f.projectName || match.projectName || "",
      leadId: f.leadId || match.leadId,
      estimateId: f.estimateId || match.estimateId,
      proposalId: f.proposalId || match.proposalId,
    }));
  }, [form.email, candidateData]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ token: string; emailQueued: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const res = await fn({ data: form });
      setResult({ token: res.token, emailQueued: !!res.emailQueued });
      qc.invalidateQueries({ queryKey: ["customer-projects"] });
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const link = result ? `${window.location.origin}/portal/claim/${result.token}` : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#0a0a0a] p-7 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X className="size-5" /></button>
        {!result ? (
          <>
            <p className="text-amber-300 text-[10px] uppercase tracking-[0.3em] font-semibold">New invitation</p>
            <h2 className="font-serif text-2xl text-white mt-1">Invite a customer</h2>
            <p className="text-sm text-zinc-500 mt-1">Creates a portal project and emails them a sign-up link.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <Field label="Find existing customer or lead">
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
                    <input
                      value={pickerQuery}
                      onChange={(e) => { setPickerQuery(e.target.value); setPickerOpen(true); }}
                      onFocus={() => setPickerOpen(true)}
                      onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
                      placeholder="Search by name, email, or phone…"
                      className={inputCls + " pl-9"}
                    />
                  </div>
                  {pickerOpen && (filtered.length > 0 || candidatesLoading || candidatesError) && (
                    <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-white/10 bg-[#0a0a0a] shadow-xl">
                      {candidatesLoading && filtered.length === 0 && (
                        <div className="px-3 py-3 text-xs text-zinc-500 inline-flex items-center gap-2">
                          <Loader2 className="size-3 animate-spin" /> Loading matches…
                        </div>
                      )}
                      {candidatesError && filtered.length === 0 && (
                        <div className="px-3 py-3 text-xs text-red-400">Could not load customers or leads.</div>
                      )}
                      {filtered.map((i) => (
                        <button
                          key={i.key}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); pick(i); }}
                          className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm text-white truncate">{i.name || i.email}</span>
                            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0 ${i.kind === "customer" ? "bg-amber-300/15 text-amber-200 border border-amber-300/25" : "bg-sky-400/15 text-sky-200 border border-sky-400/25"}`}>{i.kind}</span>
                          </div>
                          {i.name && <p className="text-[11px] text-zinc-500 truncate">{i.email}</p>}
                          {i.phone && <p className="text-[11px] text-zinc-600 truncate">{i.phone}</p>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </Field>
              <Field label="Customer name"><input required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} className={inputCls} /></Field>
              <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
              <Field label="Project name"><input required value={form.projectName} onChange={(e) => setForm({ ...form, projectName: e.target.value })} placeholder="e.g. Interior repaint" className={inputCls} /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone (optional)"><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
                <Field label="Address (optional)"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} /></Field>
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
              <button type="submit" disabled={busy} className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40 inline-flex items-center justify-center gap-2 mt-2">
                {busy && <Loader2 className="size-4 animate-spin" />}
                <Mail className="size-4" /> Create & send invitation
              </button>
            </form>
          </>
        ) : (
          <>
            <CheckCircle2 className="size-8 text-amber-300" />
            <h2 className="font-serif text-2xl text-white mt-3">Invitation sent</h2>
            <p className="text-sm text-zinc-400 mt-2">
              {result.emailQueued ? "An email has been queued." : "The invitation link was created, but the email could not be queued."} You can also share this link directly:
            </p>
            <div className="mt-4 flex items-center gap-2 bg-black/40 border border-white/10 rounded-xl p-3">
              <code className="text-xs text-amber-200 truncate flex-1">{link}</code>
              <button onClick={() => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="text-xs text-zinc-300 hover:text-amber-300 inline-flex items-center gap-1 shrink-0">
                <Copy className="size-3" /> {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <button onClick={onClose} className="w-full mt-5 bg-white/5 border border-white/10 text-white py-3 rounded-full text-sm hover:bg-white/10">Done</button>
          </>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-[15px] text-white focus:border-amber-300/50 outline-none";
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}